"use client";

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  useForm,
  SubmitHandler,
  useFieldArray,
  Resolver,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  TraineeDetailsFormModalProps,
  TraineeDetailsFormValues,
  AdditionalChannelFormValues,
  YouTubeChannelFormValues,
  InstagramChannelFormValues,
} from "./TraineeDetailsFormModal.types";
import {
  traineeDetailsSchema,
  type TraineeDetailsFormSchema,
} from "./trainee-details-form.schema";

export type { TraineeDetailsFormSchema };
export { traineeDetailsSchema };

const normalizeYouTubeChannels = (
  channels?: Partial<YouTubeChannelFormValues>[]
): YouTubeChannelFormValues[] => {
  if (!channels || channels.length === 0) {
    return [
      {
        channelName: "",
        link: "",
        language: "",
      },
    ];
  }

  return channels.map((channel) => ({
    channelName: channel.channelName ?? "",
    link: channel.link ?? "",
    language: channel.language ?? "",
  }));
};

const normalizeInstagramChannels = (
  channels?: Partial<InstagramChannelFormValues>[]
): InstagramChannelFormValues[] => {
  if (!channels || channels.length === 0) {
    return [
      {
        channelName: "",
        link: "",
        language: "",
      },
    ];
  }

  return channels.map((channel) => ({
    channelName: channel.channelName ?? "",
    link: channel.link ?? "",
    language: channel.language ?? "",
  }));
};

const normalizeOtherChannels = (
  channels?: Partial<AdditionalChannelFormValues>[]
): AdditionalChannelFormValues[] => {
  if (!channels || channels.length === 0) {
    return [];
  }

  return channels.map((channel) => ({
    name: channel.name ?? "",
    link: channel.link ?? "",
    platform: channel.platform ?? "",
  }));
};

export const TraineeDetailsFormModal: React.FC<
  TraineeDetailsFormModalProps
> = ({ isOpen, onClose, onSubmit, initialValues, isUpdateMode }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<TraineeDetailsFormSchema>({
    resolver: zodResolver(
      traineeDetailsSchema
    ) as Resolver<TraineeDetailsFormSchema>,
    mode: "onSubmit",
    reValidateMode: "onChange",
    shouldFocusError: true,
    defaultValues: {
      name: "",
      email: "",
      whatsappNumber: "",
      dateOfBirth: "",
      profession: "",
      experienceYears: "",
      education: "",
      location: "",
      ishaPrograms: "",
      youtubeChannels: normalizeYouTubeChannels(
        initialValues?.youtubeChannels
      ),
      instagramChannels: normalizeInstagramChannels(
        initialValues?.instagramChannels
      ),
      audiencePersona: initialValues?.audiencePersona ?? "",
      channelGenre: initialValues?.channelGenre ?? "",
      videoFormat: initialValues?.videoFormat ?? "",
      strengths: initialValues?.strengths ?? "",
      contentCreationChallenges:
        initialValues?.contentCreationChallenges ?? "",
      postingRegularly: undefined,
      postingChallenge: "",
      channelGrowthJourney: initialValues?.channelGrowthJourney ?? "",
      otherChannels: normalizeOtherChannels(initialValues?.otherChannels),
      channelsMonetized: initialValues?.channelsMonetized ?? undefined,
      monetizedChannelsDetails: initialValues?.monetizedChannelsDetails ?? "",
      teamNotes: initialValues?.teamNotes ?? "",
    },
  });

  const {
    fields: youtubeChannelFields,
    append: appendYouTubeChannel,
    remove: removeYouTubeChannel,
  } = useFieldArray({
    control,
    name: "youtubeChannels",
  });

  const {
    fields: instagramChannelFields,
    append: appendInstagramChannel,
    remove: removeInstagramChannel,
  } = useFieldArray({
    control,
    name: "instagramChannels",
  });

  const {
    fields: otherChannelFields,
    append: appendOtherChannel,
    remove: removeOtherChannel,
  } = useFieldArray({
    control,
    name: "otherChannels",
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        name: initialValues?.name ?? "",
        email: initialValues?.email ?? "",
        whatsappNumber: initialValues?.whatsappNumber ?? "",
        dateOfBirth: initialValues?.dateOfBirth ?? "",
        profession: initialValues?.profession ?? "",
        experienceYears: initialValues?.experienceYears ?? "",
        education: initialValues?.education ?? "",
        location: initialValues?.location ?? "",
        ishaPrograms: initialValues?.ishaPrograms ?? "",
        youtubeChannels: normalizeYouTubeChannels(
          initialValues?.youtubeChannels
        ),
        instagramChannels: normalizeInstagramChannels(
          initialValues?.instagramChannels
        ),
        audiencePersona: initialValues?.audiencePersona ?? "",
        channelGenre: initialValues?.channelGenre ?? "",
        videoFormat: initialValues?.videoFormat ?? "",
        strengths: initialValues?.strengths ?? "",
        contentCreationChallenges:
          initialValues?.contentCreationChallenges ?? "",
        postingRegularly: initialValues?.postingRegularly ?? undefined,
        postingChallenge: initialValues?.postingChallenge ?? "",
        channelGrowthJourney: initialValues?.channelGrowthJourney ?? "",
        otherChannels: normalizeOtherChannels(initialValues?.otherChannels),
        channelsMonetized: initialValues?.channelsMonetized ?? undefined,
        monetizedChannelsDetails: initialValues?.monetizedChannelsDetails ?? "",
        teamNotes: initialValues?.teamNotes ?? "",
      });
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, reset, initialValues]);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const handleFormSubmit: SubmitHandler<TraineeDetailsFormSchema> = async (
    values
  ) => {
    try {
      await onSubmit?.(values);
      onClose();
    } catch (error: any) {
      const message =
        error?.message || "Failed to save your profile. Please try again.";

      const lower = String(message).toLowerCase();

      // Map backend 409/validation messages into inline field errors.
      if (lower.includes("email")) {
        setError("email", { type: "server", message });
      } else if (
        lower.includes("whatsapp") ||
        lower.includes("mobile") ||
        lower.includes("phone")
      ) {
        setError("whatsappNumber", { type: "server", message });
      } else {
        // Fallback: show the message on email since it's the most common unique-key conflict.
        setError("email", { type: "server", message });
      }
    }
  };

  const handleInvalidSubmit = () => {
    requestAnimationFrame(() => {
      const firstError = document.querySelector(
        "#trainee-details-form .text-error-100"
      );
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const watchedChannelsMonetized = watch("channelsMonetized");
  const watchedPostingRegularly = watch("postingRegularly");

  return createPortal(
    <div className="fixed inset-0 z-[2147483647] isolate flex items-start justify-center bg-black/50 px-4 py-2 sm:py-4 overflow-y-auto">
      <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto my-auto">
        <div className="bg-[#F8F9FA] px-8 py-4">
          <div className="bg-white rounded-xl border border-[#E5E5E5] shadow-wisdom-header p-[34px]">
            <div className="flex flex-col gap-8 mb-8">
              <div className="flex flex-col gap-2">
                <h1 className="font-inter text-2xl font-semibold leading-8 text-[#2C1810]">
                  Sadhguru Wisdom Warriors – Profile
                </h1>
                <p className="font-inter text-sm font-normal leading-5 text-[#666]">
                  Please fill out all sections to complete your profile
                  registration
                </p>
              </div>
            </div>

            <form
              id="trainee-details-form"
              onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)}
              className="flex flex-col gap-12"
            >
              {/* Section 1: Personal Information */}
              <div className="flex flex-col gap-6">
                <div className="border-b border-[#E5E5E5] pb-2.5">
                  <h2 className="font-inter text-xl font-medium leading-7 text-[#2C1810]">
                    Section 1: Personal Information
                  </h2>
                </div>

                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="name"
                      className="font-inter text-base font-medium leading-6 text-[#2C1810]"
                    >
                      Full Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      placeholder="Enter your full name..."
                      className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[22px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      {...register("name")}
                    />
                    {errors.name && (
                      <span className="text-xs text-error-100">
                        {errors.name.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="email"
                      className="font-inter text-base font-medium leading-6 text-[#2C1810]"
                    >
                      Email Address<span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="Enter your email address..."
                      className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      {...register("email")}
                    />
                    {errors.email && (
                      <span className="text-xs text-error-100">
                        {errors.email.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="whatsappNumber"
                      className="font-inter text-base font-medium leading-6 text-[#2C1810]"
                    >
                      WhatsApp Number<span className="text-red-500">*</span>
                    </label>
                    <input
                      id="whatsappNumber"
                      type="tel"
                      placeholder="+91 (555) 123-4567"
                      className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[18px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      {...register("whatsappNumber")}
                    />
                    {errors.whatsappNumber && (
                      <span className="text-xs text-error-100">
                        {errors.whatsappNumber.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="dateOfBirth"
                      className="font-inter text-base font-medium leading-6 text-[#2C1810]"
                    >
                      Date of Birth<span className="text-red-500">*</span>
                    </label>
                    <input
                      id="dateOfBirth"
                      type="date"
                      className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 font-inter text-base font-normal leading-6 text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      {...register("dateOfBirth")}
                    />
                    {errors.dateOfBirth && (
                      <span className="text-xs text-error-100">
                        {errors.dateOfBirth.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="profession"
                      className="font-inter text-base font-medium leading-6 text-[#2C1810]"
                    >
                      Profession/Field of Expertise
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="profession"
                      type="text"
                      placeholder="Enter your profession or field of expertise..."
                      className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[15px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      {...register("profession")}
                    />
                    {errors.profession && (
                      <span className="text-xs text-error-100">
                        {errors.profession.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="experienceYears"
                      className="font-inter text-base font-medium leading-6 text-[#2C1810]"
                    >
                      Number of Years of Experience
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="experienceYears"
                      type="text"
                      placeholder="Enter years of experience..."
                      className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[18px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      {...register("experienceYears")}
                    />
                    {errors.experienceYears && (
                      <span className="text-xs text-error-100">
                        {errors.experienceYears.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="education"
                      className="font-inter text-base font-medium leading-6 text-[#2C1810]"
                    >
                      Education Level and Field of Study
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="education"
                      type="text"
                      placeholder="Enter your education level and field..."
                      className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[14px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      {...register("education")}
                    />
                    {errors.education && (
                      <span className="text-xs text-error-100">
                        {errors.education.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="location"
                      className="font-inter text-base font-medium leading-6 text-[#2C1810]"
                    >
                      Location (Country and City)
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="location"
                      type="text"
                      placeholder="Enter your country and city..."
                      className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-3 font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      {...register("location")}
                    />
                    {errors.location && (
                      <span className="text-xs text-error-100">
                        {errors.location.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 pb-1.5">
                    <label
                      htmlFor="ishaPrograms"
                      className="font-inter text-base font-medium leading-6 text-[#2C1810]"
                    >
                      Which Isha Programs Have You Completed?
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="ishaPrograms"
                      placeholder=""
                      className="h-[100px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 py-4 font-inter text-base font-normal leading-6 text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                      {...register("ishaPrograms")}
                    />
                    {errors.ishaPrograms && (
                      <span className="text-xs text-error-100">
                        {errors.ishaPrograms.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2: YouTube Channel Details */}
              <div className="flex flex-col gap-6">
                <div className="border-b border-[#E5E5E5] pb-2.5">
                  <h2 className="font-inter text-xl font-medium leading-7 text-[#2C1810]">
                    Section 2: YouTube Channel Details
                  </h2>
                  <p className="mt-1 font-inter text-sm font-normal leading-5 text-[#666666]">
                    At least one channel is required — YouTube, Instagram, or an
                    Additional channel (Section 5).
                  </p>
                </div>

                <div className="flex flex-col gap-6">
                  {/* Display channel validation error */}
                  {(() => {
                    // Check for custom error on youtubeChannels (from superRefine)
                    if (
                      errors.youtubeChannels &&
                      typeof errors.youtubeChannels === "object" &&
                      "message" in errors.youtubeChannels &&
                      typeof errors.youtubeChannels.message === "string"
                    ) {
                      return (
                        <div className="text-xs text-error-100">
                          {errors.youtubeChannels.message}
                        </div>
                      );
                    }
                    // Check for root-level custom error
                    if (
                      errors.youtubeChannels &&
                      typeof errors.youtubeChannels === "object" &&
                      "root" in errors.youtubeChannels &&
                      errors.youtubeChannels.root &&
                      typeof errors.youtubeChannels.root === "object" &&
                      "message" in errors.youtubeChannels.root &&
                      typeof errors.youtubeChannels.root.message === "string"
                    ) {
                      return (
                        <div className="text-xs text-error-100">
                          {errors.youtubeChannels.root.message}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {youtubeChannelFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="flex flex-col gap-4 border border-[#E5E5E5] rounded-lg p-4"
                    >
                      {youtubeChannelFields.length > 1 && (
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-inter text-sm text-[#666]">
                            YouTube Channel {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeYouTubeChannel(index)}
                            className="text-sm text-[#8B4513] hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      <div className="flex flex-col gap-2">
                        <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                          YouTube Channel Name
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Enter your YouTube channel name..."
                          className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[21px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                          {...register(`youtubeChannels.${index}.channelName`)}
                        />
                        {errors.youtubeChannels?.[index]?.channelName && (
                          <span className="text-xs text-error-100">
                            {errors.youtubeChannels[index]?.channelName?.message}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                          YouTube Channel Link
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          placeholder="https://youtube.com/@yourchannel"
                          className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[17px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                          {...register(`youtubeChannels.${index}.link`)}
                        />
                        {errors.youtubeChannels?.[index]?.link && (
                          <span className="text-xs text-error-100">
                            {errors.youtubeChannels[index]?.link?.message}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                          Main Language of Channel Content
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="English, Hindi, Tamil, etc. (mention all if multiple)"
                          className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[18px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                          {...register(`youtubeChannels.${index}.language`)}
                        />
                        {errors.youtubeChannels?.[index]?.language && (
                          <span className="text-xs text-error-100">
                            {errors.youtubeChannels[index]?.language?.message}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() =>
                        appendYouTubeChannel({
                          channelName: "",
                          link: "",
                          language: "",
                        })
                      }
                      className="flex items-center gap-2 rounded-lg border-2 border-[#D4AF37] bg-[#FEFEFE] px-7 py-4"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10.0007 3.33301V16.6663M16.6673 9.99967H3.33398"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="font-inter text-base font-medium leading-6 text-[#8B4513]">
                        Add Another YouTube Channel
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Section 3: Instagram Channel Details */}
              <div className="flex flex-col gap-6">
                <div className="border-b border-[#E5E5E5] pb-2.5">
                  <h2 className="font-inter text-xl font-medium leading-7 text-[#2C1810]">
                    Section 3: Instagram Channel Details
                  </h2>
                </div>

                <div className="flex flex-col gap-6">
                  {(() => {
                    if (
                      errors.instagramChannels &&
                      typeof errors.instagramChannels === "object" &&
                      "root" in errors.instagramChannels &&
                      errors.instagramChannels.root &&
                      typeof errors.instagramChannels.root === "object" &&
                      "message" in errors.instagramChannels.root &&
                      typeof errors.instagramChannels.root.message === "string"
                    ) {
                      return (
                        <div className="text-xs text-error-100">
                          {errors.instagramChannels.root.message}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {instagramChannelFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="flex flex-col gap-4 border border-[#E5E5E5] rounded-lg p-4"
                    >
                      {instagramChannelFields.length > 1 && (
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-inter text-sm text-[#666]">
                            Instagram Channel {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeInstagramChannel(index)}
                            className="text-sm text-[#8B4513] hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      <div className="flex flex-col gap-2">
                        <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                          Instagram Channel Name
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Enter your Instagram channel name..."
                          className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                          {...register(
                            `instagramChannels.${index}.channelName`
                          )}
                        />
                        {errors.instagramChannels?.[index]?.channelName && (
                          <span className="text-xs text-error-100">
                            {
                              errors.instagramChannels[index]?.channelName
                                ?.message
                            }
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                          Instagram Channel Link
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          placeholder="https://instagram.com/yourusername"
                          className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[11px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                          {...register(`instagramChannels.${index}.link`)}
                        />
                        {errors.instagramChannels?.[index]?.link && (
                          <span className="text-xs text-error-100">
                            {errors.instagramChannels[index]?.link?.message}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                          Main Language of Channel Content
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="English, Hindi, Tamil, etc. (mention all if multiple)"
                          className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[14px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                          {...register(`instagramChannels.${index}.language`)}
                        />
                        {errors.instagramChannels?.[index]?.language && (
                          <span className="text-xs text-error-100">
                            {errors.instagramChannels[index]?.language?.message}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() =>
                        appendInstagramChannel({
                          channelName: "",
                          link: "",
                          language: "",
                        })
                      }
                      className="flex items-center gap-2 rounded-lg border-2 border-[#D4AF37] bg-[#FEFEFE] px-7 py-4"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10.0007 3.33301V16.6663M16.6673 9.99967H3.33398"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="font-inter text-base font-medium leading-6 text-[#8B4513]">
                        Add Another Instagram Channel
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Section 4: Your main channel's overview */}
              <div className="flex flex-col gap-6">
                <div className="border-b border-[#E5E5E5] pb-2.5">
                  <h2 className="font-inter text-xl font-medium leading-7 text-[#2C1810]">
                    Section 4: Your main channel&apos;s overview
                  </h2>
                </div>

                <div className="flex flex-col gap-[13.5px]">
                  <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                    Channel Overview and Growth Journey (100 words)
                    <span className="text-red-500">*</span>
                  </label>
                  <p className="font-inter text-sm font-normal leading-5 text-[#666]">
                    Share how your channels have evolved, key milestones, and
                    future goals.
                  </p>
                  <textarea
                    placeholder="Describe your journey..."
                    maxLength={1000}
                    className="h-[120px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 py-4 font-inter text-base font-normal leading-6 text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                    {...register("channelGrowthJourney")}
                  />
                  {errors.channelGrowthJourney && (
                    <span className="text-xs text-error-100">
                      {errors.channelGrowthJourney.message}
                    </span>
                  )}
                  <span className="text-xs text-neutral-500">
                    Max 100 words (~1000 characters)
                  </span>
                </div>

                {/* Niche / audience clarity */}
                <div className="flex flex-col gap-[13.5px]">
                  <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                    Channel&apos;s Niche / Audience Clarity
                    <span className="text-red-500">*</span>
                  </label>
                  <p className="font-inter text-sm font-normal leading-5 text-[#666]">
                    Please brief about your channel&apos;s audience persona in
                    100 words.
                  </p>
                  <textarea
                    placeholder=""
                    className="h-[120px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 py-4 font-inter text-base font-normal leading-6 text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                    {...register("audiencePersona")}
                  />
                  {errors.audiencePersona && (
                    <span className="text-xs text-error-100">
                      {errors.audiencePersona.message}
                    </span>
                  )}
                </div>

                {/* Content / topics */}
                <div className="flex flex-col gap-[13.5px]">
                  <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                    Channel&apos;s Content / Topics
                    <span className="text-red-500">*</span>
                  </label>
                  <p className="font-inter text-sm font-normal leading-5 text-[#666]">
                    Describe your channel&apos;s genre (e.g., Wellness,
                    Spirituality, Education).
                  </p>
                  <textarea
                    placeholder=""
                    className="h-[120px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 py-4 font-inter text-base font-normal leading-6 text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                    {...register("channelGenre")}
                  />
                  {errors.channelGenre && (
                    <span className="text-xs text-error-100">
                      {errors.channelGenre.message}
                    </span>
                  )}
                </div>

                {/* Video format */}
                <div className="flex flex-col gap-3">
                  <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                    Channel&apos;s Video Format
                    <span className="text-red-500">*</span>
                  </label>
                  <p className="font-inter text-sm font-normal leading-5 text-[#666]">
                    Example: Vlogs, Podcast, voice-over, documentary,
                    animation, etc.
                  </p>
                  <input
                    type="text"
                    placeholder="Enter your video format style..."
                    className="h-[47px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[17px] py-[13px] font-inter text-base font-normal text-black placeholder:text-[#999] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    {...register("videoFormat")}
                  />
                  {errors.videoFormat && (
                    <span className="text-xs text-error-100">
                      {errors.videoFormat.message}
                    </span>
                  )}
                </div>

                {/* Key competencies */}
                <div className="flex flex-col gap-3">
                  <h3 className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                    Key competencies<span className="text-red-500">*</span>
                  </h3>
                  <label className="font-inter text-sm font-normal leading-5 text-[#666]">
                    Which aspects of content creation do you feel most
                    confident about?
                  </label>
                  <textarea
                    placeholder=""
                    className="h-[100px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 py-4 font-inter text-base font-normal leading-6 text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                    {...register("strengths")}
                  />
                  {errors.strengths && (
                    <span className="text-xs text-error-100">
                      {errors.strengths.message}
                    </span>
                  )}
                </div>

                {/* Challenging aspects */}
                <div className="flex flex-col gap-3">
                  <h3 className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                    Challenging aspects<span className="text-red-500">*</span>
                  </h3>
                  <label className="font-inter text-sm font-normal leading-5 text-[#666]">
                    Which aspects of content creation do you find most
                    challenging?
                  </label>
                  <textarea
                    placeholder=""
                    className="h-[120px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 py-4 font-inter text-base font-normal leading-6 text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                    {...register("contentCreationChallenges")}
                  />
                  {errors.contentCreationChallenges && (
                    <span className="text-xs text-error-100">
                      {errors.contentCreationChallenges.message}
                    </span>
                  )}
                </div>

                  {/* Are you posting regularly */}
                  <div className="flex flex-col gap-4 mt-4">
                  <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                      Are you posting regularly across your channels?
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-6 md:gap-[52px] pl-0 sm:pl-7">
                      <label className="flex items-center gap-2 font-inter text-base font-normal leading-6 text-[#2C1810] cursor-pointer">
                        <input
                          type="radio"
                          value="Yes"
                          {...register("postingRegularly")}
                          className="w-4 h-4"
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-2 font-inter text-base font-normal leading-6 text-[#2C1810] cursor-pointer">
                        <input
                          type="radio"
                          value="No"
                          {...register("postingRegularly")}
                          className="w-4 h-4"
                        />
                        No
                      </label>
                      <label className="flex items-center gap-2 font-inter text-base font-normal leading-6 text-[#2C1810] cursor-pointer">
                        <input
                          type="radio"
                          value="Sometimes"
                          {...register("postingRegularly")}
                          className="w-4 h-4"
                        />
                        Sometimes
                      </label>
                    </div>
                    {errors.postingRegularly && (
                      <span className="text-xs text-error-100">
                        {errors.postingRegularly.message}
                      </span>
                    )}
                    {watchedPostingRegularly === "No" && (
                      <div className="flex flex-col gap-2">
                        <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                          What is the main bottleneck or challenge stopping you
                          from posting regularly?
                          <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          placeholder="Describe your challenge..."
                          maxLength={1000}
                          className="h-[120px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 py-4 font-inter text-base font-normal leading-6 text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                          {...register("postingChallenge")}
                        />
                        {errors.postingChallenge && (
                          <span className="text-xs text-error-100">
                            {errors.postingChallenge.message}
                          </span>
                        )}
                      </div>
                    )}

                </div>
              </div>

              {/* Section 5: Additional Information */}
              <div className="flex flex-col gap-6">
                <div className="border-b border-[#E5E5E5] pb-2.5">
                  <h2 className="font-inter text-xl font-medium leading-7 text-[#2C1810]">
                    Section 5: Additional Information
                  </h2>
                </div>

                {/* Other channels */}
                <div className="flex flex-col gap-6">
                  <label className="font-inter font-medium text-base leading-6 text-wisdom-brown-dark">
                    If you have other channels on different social media platforms, please mention it
                  </label>

                  {otherChannelFields.length > 0 ? (
                    <div className="flex flex-col gap-6">
                      {otherChannelFields.map((channel, index) => (
                        <div
                          key={channel.id}
                          className="flex flex-col gap-4 border border-[#E5E5E5] rounded-lg p-4"
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-inter text-sm text-[#666]">
                              Channel {index + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeOtherChannel(index)}
                              className="text-sm text-[#8B4513] hover:underline"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                              Channel Name
                            </label>
                            <input
                              type="text"
                              placeholder="Enter your channel name..."
                              className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[15px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                              {...register(`otherChannels.${index}.name`)}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                              Channel Link
                            </label>
                            <input
                              type="url"
                              placeholder="Enter your channel URL..."
                              className="h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-[18px] font-inter text-base font-normal leading-6 text-black placeholder:text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                              {...register(`otherChannels.${index}.link`)}
                            />
                            {errors.otherChannels?.[index]?.link && (
                              <span className="text-xs text-error-100">
                                {errors.otherChannels[index]?.link?.message}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                              Platform
                            </label>
                            <div className="relative h-[50px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE]">
                              <select
                                className="h-full w-full appearance-none rounded-lg bg-transparent px-[22px] font-inter text-base font-normal text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                                {...register(`otherChannels.${index}.platform`)}
                              >
                                <option value="">Select platform...</option>
                                <option value="Facebook">Facebook</option>
                                <option value="Twitter">Twitter</option>
                                <option value="TikTok">TikTok</option>
                                <option value="LinkedIn">LinkedIn</option>
                                <option value="Other">Other</option>
                              </select>
                              <svg
                                className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4 6L8 10L12 6"
                                  stroke="#666666"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-[#E5E5E5] bg-[#FEFEFE] px-4 py-3 text-sm text-[#666]">
                      No other channels added yet.
                    </div>
                  )}

                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() =>
                        appendOtherChannel({
                          name: "",
                          link: "",
                          platform: "",
                        })
                      }
                      className="flex items-center gap-2 rounded-lg border-2 border-[#D4AF37] bg-[#FEFEFE] px-7 py-4"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10.0007 3.33301V16.6663M16.6673 9.99967H3.33398"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="font-inter text-base font-medium leading-6 text-[#8B4513]">
                        Add Other Channel
                      </span>
                    </button>
                  </div>
                </div>

                {/* Monetization */}
                <div className="flex flex-col gap-4">
                  <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                    Are any of your channels monetized?
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-[52px] pl-7">
                    <label className="flex items-center gap-2 font-inter text-base font-normal leading-6 text-[#2C1810] cursor-pointer">
                      <input
                        type="radio"
                        value="Yes"
                        {...register("channelsMonetized")}
                        className="w-4 h-4"
                      />
                      Yes
                    </label>
                    <label className="flex items-center gap-2 font-inter text-base font-normal leading-6 text-[#2C1810] cursor-pointer">
                      <input
                        type="radio"
                        value="No"
                        {...register("channelsMonetized")}
                        className="w-4 h-4"
                      />
                      No
                    </label>
                  </div>
                  {errors.channelsMonetized && (
                    <span className="text-xs text-error-100">
                      {errors.channelsMonetized.message}
                    </span>
                  )}
                  {watchedChannelsMonetized === "Yes" && (
                    <div className="flex flex-col gap-2">
                      <textarea
                        placeholder="Please provide the monetized channel name(s) and link(s)..."
                        maxLength={1000}
                        className="h-[120px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 py-4 font-inter text-base font-normal leading-6 text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                        {...register("monetizedChannelsDetails")}
                      />
                      {errors.monetizedChannelsDetails && (
                        <span className="text-xs text-error-100">
                          {errors.monetizedChannelsDetails.message}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Message to team */}
                <div className="flex flex-col gap-[13.5px]">
                  <label className="font-inter text-base font-medium leading-6 text-[#2C1810]">
                    Anything you want to share with the team (1000 words)
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    placeholder="Share any additional updates, requests, or information for the team..."
                    maxLength={1000}
                    className="h-[150px] rounded-lg border border-[#D4AF37] bg-[#FEFEFE] px-5 py-4 font-inter text-base font-normal leading-6 text-black focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                    {...register("teamNotes")}
                  />
                  {errors.teamNotes && (
                    <span className="text-xs text-error-100">
                      {errors.teamNotes.message}
                    </span>
                  )}
                  <span className="text-xs text-neutral-500">
                    Max 1000 characters
                  </span>
                </div>
              </div>

              {/* Terms and Submit */}
              <div className="flex flex-col gap-6 border-t border-[#E5E5E5] pt-[26px]">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 rounded-lg border border-[#8B4513] bg-white py-4 font-inter text-base font-medium leading-6 text-center text-[#8B4513] hover:bg-[#F5F5DC] focus:outline-none focus:ring-2 focus:ring-[#8B4513] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 rounded-lg bg-[#8B4513] py-4 font-inter text-base font-medium leading-6 text-center text-[#F5F5DC] hover:bg-[#6B4423] focus:outline-none focus:ring-2 focus:ring-[#8B4513] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Profile"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    ,
    document.body
  );
};

export default TraineeDetailsFormModal;